import { useEffect, useMemo, useState } from "react";
import { Map, List, FileText } from "lucide-react";
import { ModemSidebar } from "./components/ModemSidebar.tsx";
import { ModemMap } from "./components/ModemMap.tsx";
import { ModemTable } from "./components/ModemTable.tsx";
import { ModemDetail } from "./components/ModemDetail.tsx";
import { KPIGauge } from "./components/KPIGauge.tsx";
import type { Modem, Area } from "./types/types.ts";
import axios from "./util/axiosInstance";
import { ToastContainer } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { MainReport } from "./components/reports/MainReport.tsx";

// Map
interface Device {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
}

interface EngineData {
  id?: string;
  deviceId?: string;
  timestamp?: string;
  [key: string]: any;
}

interface AppsPRCProps {
  setToken: (token: string | null) => void;
}

const isOlderThan24Hours = (sampleTime: string): boolean => {
  if (!sampleTime || sampleTime === "N/A") return true;

  try {
    const sampleDate = new Date(sampleTime);
    const currentDate = new Date();
    const diffInHours =
      (currentDate.getTime() - sampleDate.getTime()) / (1000 * 60 * 60);
    return diffInHours > 24;
  } catch (error) {
    console.error("Error parsing date:", error);
    return true;
  }
};

const formatTime = (timeString?: string): string => {
  if (!timeString || timeString === "N/A") return "N/A";
  try {
    const date = new Date(timeString);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting date:", error);
    return "N/A";
  }
};

const getOperationalStatus = (
  area: Area
): "invalid-data" | "online" | "offline" | "alarm" | "no-contact" => {
  if (area.SDcode == -1) {
    return "invalid-data";
  }
  if (area.SDcode == 0 && !isOlderThan24Hours(area.time)) {
    return "online";
  }
  if (area.SDcode == 0 && isOlderThan24Hours(area.time)) {
    return "offline";
  }
  if (area.SDcode !== 0 && area.SDcode !== -1) {
    return "alarm";
  }
  return "no-contact";
};

const areaToModem = (areas: Area[], devices: Device[]): Modem[] => {
  return areas.map((area) => {
    // Find matching device by ID to get coordinates
    const device = devices.find((d) => d.id === area.id);

    return {
      id: area.id,
      name: area.name,
      unit_name: area.id,
      operational_status: getOperationalStatus(area),
      last_update: area.time,
      latitude: device?.latitude || 0,
      longitude: device?.longitude || 0,
      areaData: area,
    };
  });
};

type TabType = "overview" | "list" | "reports";

export default function AppsPRC({ setToken }: AppsPRCProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<TabType>("overview");
  const [modems, setModems] = useState<Modem[]>([]);
  const [mapModems, setMapModems] = useState<Modem[]>([]);
  const [selectedModemId, setSelectedModemId] = useState<string | undefined>();
  const [detailModem, setDetailModem] = useState<Modem | null>(null);
  const [engineData, setEngineData] = useState<Record<string, EngineData>>({});
  const [loadingDeviceId, setLoadingDeviceId] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [groupname, setGroupName] = useState<string | null>(null);

  const [stats, setStats] = useState({
    total: 0,
    running: 0,
    alarm: 0,
    stopped: 0,
    runningPercentage: 0,
    alarmPercentage: 0,
  });
  const [groupId, setGroupId] = useState<string | null>(
    sessionStorage.getItem("previewGroupId") || sessionStorage.getItem("customerGroupId")
  );
  const [isPreviewMode] = useState(() => Boolean(sessionStorage.getItem("previewGroupId")));
  const [groupIdReady, setGroupIdReady] = useState(false);
  // const isPreview = Boolean(groupId);

  useEffect(() => {
  const resolveGroupId = async () => {
    const sessionId = sessionStorage.getItem("previewGroupId");
    if (sessionId) {
      setGroupId(sessionId);
      setGroupIdReady(true);
      return; // safe to return here — groupId is known
    }

    const cachedCustomerGroupId = sessionStorage.getItem("customerGroupId");
      if (cachedCustomerGroupId) {
        setGroupId(cachedCustomerGroupId);
        setGroupIdReady(true);
        return;
      }

    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) return;
      const user = JSON.parse(storedUser);
      const res = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/users/${user.id}`
      );
      const userGroupId = res.data.group?.id || res.data.groupId;
      setUsername(res.data.username); // set username in customer path
      if (userGroupId) {
        setGroupId(userGroupId);
        sessionStorage.setItem("customerGroupId", userGroupId);
      }
    } catch (err) {
      console.error("Error resolving group:", err);
    } finally {
      setGroupIdReady(true); // always mark ready, even on error
    }
  };
  resolveGroupId();
}, []);

  const fetchUserName = async () => {
    try {
      const storedUser = localStorage.getItem("user");

      if (!storedUser) {
        console.error("No user found in localStorage");
        return null; // or handle differently
      }

      const user = JSON.parse(storedUser);
      const userId: string = user.id;
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/users/${userId}`
      );
      setUsername(response.data.username);
    } catch (error) {
      console.error(`Error fetching username`, error);
    } finally {
    }
  };

  const fetchGroupName = async () => {
    try {
      if (!groupId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/groups/${groupId}`
      );
      // console.log(response.data.name);
      setGroupName(response.data.name);
    } catch (error) {
      console.error(`Error fetching username`, error);
    } finally {
    }
  };
  // const [chartData, setChartData] = useState<Record<string, any>>({});
  const fetchEngineData = async (deviceId: string) => {
    try {
      setLoadingDeviceId(deviceId);
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/engine-data/${deviceId}`
      );

      // console.log("-------------------------------response: ", response);

      setEngineData((prev) => ({
        ...prev,
        [deviceId]: response.data,
      }));

      // console.log(`Engine data for ${deviceId}:`, response.data);
    } catch (error) {
      console.error(
        `Error fetching engine data for device ${deviceId}:`,
        error
      );
      setEngineData((prev) => ({
        ...prev,
        [deviceId]: { error: "Failed to load engine data" },
      }));
    } finally {
      setLoadingDeviceId(null);
    }
  };
  useEffect(() => {
    if (!groupIdReady) return;
    const init = async () => {
      setLoading(true); // show spinner once
      await fetchAreas(); // wait for first load
      setLoading(false); // hide spinner after data loaded
    };

    init(); // run it once at startup

    // Continue polling every 30s without triggering loading spinner again
    // const intervalId = setInterval(fetchAreas, 30000);

    // return () => clearInterval(intervalId);
  }, [groupIdReady]);

  const fetchAreas = async () => {
    // setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/area-status`,
        {
          params: groupId ? { groupId } : {},
        }
      );

      const areas: Area[] = response.data.data.map((area: any) => ({
        id: area.id,
        name: area.name,
        time: area.time,
        regionroute: area.regionroute,
        CtrlType: area.CtrlType === "WellPro II" ? "WP" : area.CtrlType,
        SDcode: area.SDcode,
        SDcodeDesc: area.SDcodeDesc,
        state: area.state,
        stateDesc: area.stateDesc,
        status: area.status,
        regionName: area.regionName,
        routeName: area.routeName,
      }));

      const devicesResponse = await axios.get(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/alldeviceslatlon`,
        {
          params: groupId ? { groupId } : {},
        }
      );

      const devices: Device[] = devicesResponse.data.map((device: any) => ({
        id: device.id,
        name: device.name,
        latitude: parseFloat(device.latitude) || 0,
        longitude: parseFloat(device.longitude) || 0,
      }));

      // Convert areas to modems
      const modemData = areaToModem(areas, devices);

      setModems(modemData);

      const mapModemsWithCoords: Modem[] = devices
        .filter((device) => device.latitude !== 0 && device.longitude !== 0)
        .map((device) => {
          // Find matching area data for status
          const area = areas.find((a) => a.id === device.id);
          return {
            id: device.id,
            name: device.name,
            unit_name: device.id,
            operational_status: area ? getOperationalStatus(area) : "no-contact",
            last_update: area?.time || new Date().toISOString(),
            latitude: device.latitude,
            longitude: device.longitude,
          };
        });

      setMapModems(mapModemsWithCoords);

      // Calculate statistics
      const total = modemData.length;
      const running = modemData.filter(
        (m) => m.operational_status === "online"
      ).length;
      const alarm = modemData.filter(
        (m) => m.operational_status === "alarm"
      ).length;
      const stopped = modemData.filter(
        (m) => m.operational_status === "offline"
      ).length;

      setStats({
        total,
        running,
        alarm,
        stopped,
        runningPercentage: total > 0 ? Math.round((running / total) * 100) : 0,
        alarmPercentage: total > 0 ? Math.round((alarm / total) * 100) : 0,
      });
      setLoading(false);
    } catch (err) {
      console.error("Error fetching areas:", err);
    }
  };

  useEffect(() => {
    if (!groupId) return;
    fetchGroupName();
    fetchUserName();
  }, [groupId]);

  const handleModemSelect = (modemId: string) => {
    setSelectedModemId(modemId);
    const modem = modems.find((m) => m.id === modemId);
    console.log("modem : ", modem);
    if (modem) {
      setDetailModem(modem);
      // Fetch engine data when modem is selected
      fetchEngineData(modemId);
      //  fetchChartData(modemId);
    }
  };

  const handleModemClick = (modemId: string) => {
    const modem = modems.find((m) => m.id === modemId);
    if (modem) {
      setDetailModem(modem);
      setSelectedModemId(modemId);
      setActiveTab("overview");
      // Fetch engine data when modem is clicked
      fetchEngineData(modemId);
      //  fetchChartData(modemId);
    }
  };

  // Update ModemDetail to show engine data
   const enhancedDetailModem = useMemo(() =>
      detailModem
        ? {
            ...detailModem,
            engineData: engineData[detailModem.id],
            engineDataLoading: loadingDeviceId === detailModem.id,
          }
        : null,
      [detailModem, engineData, loadingDeviceId]
    );

  //const reports = []; // You can implement this similarly
  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-950">
        <div className="flex flex-col items-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-400 mt-4 text-sm">Loading data...</p>
        </div>
      </div>
    );
  }
  const Stat = ({
    label,
    value,
    color = "text-white",
  }: {
    label: string;
    value: number;
    color?: string;
  }) => (
    <div className="text-center">
      <div className="text-xs text-slate-400">{label}</div>
      <div className={`text-lg sm:text-xl font-bold ${color}`}>{value}</div>
    </div>
  );
  return (
    <div className="flex h-screen bg-slate-950 overflow-hidden">
      {/* ================= MOBILE OVERLAY ================= */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <aside
        className={`
    fixed inset-y-0 z-40 w-72 bg-slate-900
    transition-all duration-300 ease-in-out

    ${sidebarOpen ? "left-0" : "-left-full"}

    lg:static lg:translate-x-0 lg:left-0 lg:block
  `}
      >
        {/* Close button */}
        <div className="flex justify-end p-3 lg:hidden">
          <button
            className="text-slate-300 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            ✕
          </button>
        </div>

        <ModemSidebar
          modems={modems}
          selectedModemId={selectedModemId}
          onModemSelect={(id) => {
            handleModemSelect(id);
            setSidebarOpen(false);
          }}
        />
      </aside>

      {/* ================= MAIN ================= */}
      <div className="flex flex-1 flex-col overflow-hidden lg:ml-8">
        {/* ================= HEADER ================= */}
        {/* <div className="bg-gradient-to-r from-red-600 to-red-600 animate-pulse p-2">
          <p style={{fontSize:"20px", fontWeight:"bold"}}>SMS Alerts Ending August 31, 2026. To continue receiving notifications, please download our new mobile app. <a href="https://play.google.com/store/apps/details?id=com.pytronix.MyWellAlertApp&pcampaignid=web_share" target="_blank"  style={{color:"#62b9ff"}}>[For Android]</a> <a href="https://apps.apple.com/tn/app/wellalert/id6758392078" style={{color:"#62b9ff"}} target="_blank">[For iOS]</a></p>
        </div> */}
        <header className="bg-slate-900 border-b border-slate-800 px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            {/* LEFT */}
            <div className="flex items-center gap-3">
              {/* Hamburger */}
              <button
                className="lg:hidden p-2 rounded text-white hover:bg-slate-800"
                onClick={() => setSidebarOpen(true)}
              >
                ☰
              </button>

              <div>
                <h1 className="text-lg sm:text-2xl font-bold text-white">
                  {groupname}
                </h1>
                <p className="text-xs sm:text-sm text-slate-400">{username}</p>
              </div>
            </div>

            {/* STATS */}
            <div className="grid grid-cols-4 sm:grid-cols-4 gap-4">
              <Stat label="Total" value={stats.total} />
              <Stat
                label="Running"
                value={stats.running}
                color="text-green-500"
              />
              <Stat label="Alarms" value={stats.alarm} color="text-red-500" />
              <Stat
                label="Missed"
                value={stats.stopped}
                color="text-gray-400"
              />
            </div>

            {/* LOGOUT */}
            {!isPreviewMode  && (
              <button
                onClick={async () => {
                  try {
                    await axios.post(
                      `${process.env.REACT_APP_API_GATEAWAY}/users/logout`,
                      {},
                      { withCredentials: true }
                    );
                  } finally {
                    localStorage.clear();
                    sessionStorage.clear();
                    setToken(null);
                    navigate("/signin", { replace: true });
                  }
                }}
                className="self-start lg:self-auto px-4 py-2 text-sm font-bold
                text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Logout
              </button>
            )}
          </div>

          {/* TABS */}
          <div className="flex flex-wrap gap-2 mt-4">
            {[
              ["overview", "Home Overview", Map],
              ["list", "Fleet", List],
              ["reports", "Reports", FileText],
            ].map(([key, label, Icon]: any) => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 py-2 text-sm rounded
                ${
                  activeTab === key
                    ? "bg-blue-600 text-white"
                    : "bg-slate-800 text-slate-300 hover:bg-slate-700"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </header>

        {/* ================= CONTENT ================= */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-950">
          {/* OVERVIEW */}
          {activeTab === "overview" && !detailModem && (
            <>
              {/* KPI */}
              <div className="hidden md:flex flex-wrap justify-center gap-4 mb-6">
                <KPIGauge
                  value={stats.runningPercentage}
                  label="Running Modems"
                  color="#10b981"
                />
                <KPIGauge
                  value={stats.alarmPercentage}
                  label="Alarm Modems"
                  color="#ef4444"
                />
                <KPIGauge
                  value={
                    stats.total
                      ? Math.round((stats.stopped / stats.total) * 100)
                      : 0
                  }
                  label="Missed Com Modems"
                  color="#6b7280"
                />
              </div>

              {/* MAP + UPDATES */}
              <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                <div className="xl:col-span-2 bg-slate-900 border border-slate-800 p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Fleet Map
                  </h3>
                  <div className="h-[300px] sm:h-[400px] xl:h-[500px]">
                    <ModemMap
                      modems={mapModems}
                      selectedModemId={selectedModemId}
                    />
                  </div>
                </div>

                <div className="bg-slate-900 border border-slate-800 p-4">
                  <h3 className="text-lg font-semibold text-white mb-4">
                    Recent Updates
                  </h3>
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {modems
                      .sort(
                        (a, b) =>
                          new Date(b.last_update).getTime() -
                          new Date(a.last_update).getTime()
                      )
                      .slice(0, 15)
                      .map((modem) => (
                        <button
                          key={modem.id}
                          onClick={() => handleModemClick(modem.id)}
                          className="w-full p-3 bg-slate-800 hover:bg-slate-700 text-left"
                        >
                          <div className="text-sm font-medium text-white truncate">
                            {modem.name}
                          </div>
                          <div className="text-xs text-slate-400">
                            {formatTime(modem.last_update)}
                          </div>
                        </button>
                      ))}
                  </div>
                </div>
              </div>

              {/* TABLE */}
              <h3 className="text-lg font-semibold text-white mt-10 mb-4">
                All Units
              </h3>
              <ModemTable modems={modems} onModemClick={handleModemClick} />
            </>
          )}

          {/* DETAIL */}
          {activeTab === "overview" && enhancedDetailModem && (
            <ModemDetail
              modem={enhancedDetailModem}
              onClose={() => setDetailModem(null)}
              isFullView
            />
          )}

          {/* LIST */}
          {activeTab === "list" && (
            <>
              <h2 className="text-2xl font-bold text-white mb-6">Modem List</h2>
              <ModemTable modems={modems} onModemClick={handleModemClick} />
            </>
          )}

          {/* REPORTS */}
          {activeTab === "reports" && (
            <>
              <h2 className="text-2xl font-bold text-white mb-6">Reports</h2>
              <MainReport />
            </>
          )}
        </main>
      </div>

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
}

// export default Appsss;
